import { CircularProgress, Snackbar } from "@mui/material";
import { db } from "config/config";
import {
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  or,
  and,
  orderBy,
  query,
  startAfter,
  endBefore,
  where,
} from "firebase/firestore";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import tablesDataControl from "config/advancedTableColumnsControl.json";
import { Context } from "context/Wrapper";
const searchLimit = tablesDataControl?.searchMinLength;

export const listenAndFilterTo = (props) => {
  const { setData, user, condition, setContext, collectionName } = props;

  const conditions = props?.conditions || [],
    setLastRef = props?.setLastRef || (() => { });

  if (!(user?.groupId?.length > 0 && user?.userId?.length > 0)) {
    setData([]);
    return () => { };
  }

  try {
    const q = condition
      ? query(
        collection(db, collectionName),
        or(
          where("groupId", "==", user?.groupId),
          where("userId", "==", user.userId)
        ),
        ...conditions
      )
      : query(
        collection(db, collectionName),
        where("userId", "==", user.userId),
        ...conditions
      );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (querySnapshot.docs?.length > 0)
        setLastRef(querySnapshot.docs[querySnapshot.docs.length - 1]);
      const newDocs = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      setData(newDocs);
    });

    return unsubscribe;
  } catch (err) {
    console.log(err);
    setData([]);

    setContext({ snackbar: <Snackbar open={true} message={err?.message} /> });

    setTimeout(() => {
      setContext({ snackbar: null });
    }, 4000);

    return () => { };
  }
};


export const useAdvancedDataListener = ({
  user,
  condition,
  fetchAll,
  collectionName,
  fromBuildingReferencesJobDetails,
  flagged,
  contactExtraFilters,
  defaultSortField,
  defaultSortOrder,

}) => {
  const context = useContext(Context);
  const [params] = useSearchParams();
  const sortField = params.get("sort") || defaultSortField || documentId();
  const sortOrder = params.get("order") || defaultSortOrder || "asc";
  const sortConstraint = orderBy(sortField, sortOrder);

  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [lastVisibleDocs, setLastVisibleDocs] = useState([]);
  const [firstVisibleDocs, setFirstVisibleDocs] = useState([]);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  const buildingIdCondition = fromBuildingReferencesJobDetails?.buildingId
    ? [where("building", "==", fromBuildingReferencesJobDetails.buildingId)]
    : [];

  const extraConditions = fetchAll
    ? []
    : condition
      ? [
        or(
          where("groupId", "==", user?.groupId),
          where("userId", "==", user?.userId)
        ),
      ]
      : [where("userId", "==", user?.userId)];

  let extraFilterConditions = [];
  if (contactExtraFilters) {
    extraFilterConditions = Object.keys(contactExtraFilters).map((filter) => {
      if (contactExtraFilters[filter]) {
        return where(filter, "==", true);
      }
      return null;
    }).filter(Boolean);
  }

  let combinedConditions;
  if (fromBuildingReferencesJobDetails?.fromBuildingReferencesJob) {
    if (flagged) {
      combinedConditions = [and(...[...extraConditions, ...buildingIdCondition, ...[where("flagged", "==", true)]])];
    } else {
      combinedConditions = [and(...[...extraConditions, ...buildingIdCondition])];
    }
  } else {
    if (flagged) {
      combinedConditions = [and(...[...extraConditions, ...[where("flagged", "==", true)]])];
    } else if (extraFilterConditions?.length != 0) {
      combinedConditions = [and(...extraConditions, or(...extraFilterConditions))];
    }
    else {
      combinedConditions = [and(...[...extraConditions])];
    }
  }

  useEffect(() => {
    setLastVisibleDocs([]);
    setFirstVisibleDocs([]);
    setAllDataLoaded(false);
    setData([]);
  }, [condition, fromBuildingReferencesJobDetails?.buildingId, flagged]);
  
  useEffect(() => {
    setLastVisibleDocs([]);
    setFirstVisibleDocs([]);
    setAllDataLoaded(false);
  }, [params.get("search")]);

  useEffect(() => {
    console.log(`useAdvancedDataListener - Starting fetch for ${collectionName}`);
    console.log(`Params: page=${params.get("page")}, search=${params.get("search")}`);
    console.log(`User: ${user?.userId}, Group: ${user?.groupId}`);

    if (!(user?.groupId?.length > 0 && user?.userId?.length > 0)) {
      console.log('User not ready yet');
      return;
    }

    fetchTotalItems();
    fetchPage(params?.get("page") || 0);
  }, [user, `${params}`, condition, fromBuildingReferencesJobDetails?.buildingId, flagged, contactExtraFilters]);

  const fetchTotalItems = async () => {
    console.time(`fetchTotalItems-${collectionName}`);
    try {
      const coll = query(collection(db, collectionName), ...combinedConditions);
      const snapshot = await getCountFromServer(coll);
      const itemCount = snapshot.data().count;
      setCount(itemCount);
      console.log(`Total items count: ${itemCount}`);
    } catch (error) {
      console.error("Error fetching total items:", error);
    } finally {
      console.timeEnd(`fetchTotalItems-${collectionName}`);
    }
  };

  const fetchPage = async (page) => {

    console.time(`fetchPage-${collectionName}-${page}`);
    context?.setState({
      snackbar: (
        <CircularProgress
          size={46}
          style={{
            position: "fixed",
            right: "10%",
            bottom: "5%",
            zIndex: 9,
          }}
        />
      ),
    });

    const pageSize = Number(params.get("pageSize")) || tablesDataControl?.defaultRowsLimit || 50;

    console.log(`Fetching page ${page} with pageSize ${pageSize}`);

    const isSearch = params.get("search")?.length >= searchLimit;
    console.log(`Is search mode: ${isSearch}`);

    let queryRef;
    try {
      if (isSearch) {
        console.log('Search mode: Loading ALL data for search');
        queryRef = query(
          collection(db, collectionName),
          sortConstraint,
          ...combinedConditions
        );
        setAllDataLoaded(true);
      } else if (page > 0 && lastVisibleDocs[page - 1]) {
        console.log('Fetching next page');
        queryRef = query(
          collection(db, collectionName),
          sortConstraint,
          startAfter(lastVisibleDocs[page - 1]),
          limit(pageSize),
          ...combinedConditions
        );
      } else if (page > 0 && firstVisibleDocs[page]) {
        console.log('Fetching previous page');
        queryRef = query(
          collection(db, collectionName),
          sortConstraint,
          endBefore(firstVisibleDocs[page]),
          limit(pageSize),
          ...combinedConditions
        );
      } else {
        console.log('Fetching first page');
        queryRef = query(
          collection(db, collectionName),
          sortConstraint,
          limit(pageSize),
          ...combinedConditions
        );
      }

      console.time(`firestoreQuery-${collectionName}-${page}`);
      const snap = await getDocs(queryRef);
      console.timeEnd(`firestoreQuery-${collectionName}-${page}`);

      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log(`Fetched ${docs.length} documents for page ${page}`);

      setData(docs);

      if (snap.docs.length > 0) {
        lastVisibleDocs[page] = snap.docs[snap.docs.length - 1];
        firstVisibleDocs[page] = snap.docs[0];
        setLastVisibleDocs([...lastVisibleDocs]);
        setFirstVisibleDocs([...firstVisibleDocs]);
      }

    } catch (error) {
      console.error("Error fetching page:", error);
    } finally {
      setTimeout(() => context?.setState({ snackbar: null }), 500);
      console.timeEnd(`fetchPage-${collectionName}-${page}`);
    }
  };

  const refetch = () => {
    console.log('Refetching data');
    setLastVisibleDocs([]);
    setFirstVisibleDocs([]);
    setAllDataLoaded(false);
    fetchTotalItems();
    fetchPage(0);
  };

  return { data, count, refetch };
};


export const useFetchCollectionDocs = ({
  collectionName,
  conditions = [],
  setContext = () => { },
}) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    console.log(`useFetchCollectionDocs called for ${collectionName} with conditions:`, conditions);
    console.time(`useFetchCollectionDocs-${collectionName}`);

    if (collectionName === "contacts" || collectionName === "buildings") {
      console.log(`Skipping global fetch for ${collectionName} - will be fetched on-demand`);
      console.timeEnd(`useFetchCollectionDocs-${collectionName}`);
      return;
    }

    (async () => {
      try {
        if (conditions?.length === 0) {
          console.log(`No conditions provided for ${collectionName}`);
          return;
        }

        console.log(`Fetching ${collectionName} with conditions`);
        const data = await getDocs(
          query(collection(db, collectionName), ...conditions)
        );

        if (data.empty) {
          console.log(`No data found for ${collectionName}`);
          return;
        }

        const docs = data.docs?.map((doc) => ({
          ...doc?.data(),
          id: doc.id,
        })) || [];

        console.log(`Fetched ${docs.length} ${collectionName}`);
        setData(docs);
      } catch (error) {
        setContext({
          snackbar: <Snackbar open={true} message={error?.message} />,
        });

        setTimeout(() => {
          setContext({ snackbar: null });
        }, 4000);

        console.log("error", error);
      } finally {
        console.timeEnd(`useFetchCollectionDocs-${collectionName}`);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, JSON.stringify(conditions)]);

  return data;
};